import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Avatar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  MenuItem,
  Divider,
  Select,
  Chip,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import {
  Dashboard,
  LocalShipping,
  UploadFile,
  Payments,
  Notifications,
  Menu as MenuIcon,
  Logout,
  DirectionsBoat,
  CallMerge,
  LocalOffer,
  CardGiftcard,
  WorkspacePremium,
  ChevronRight,
  Inventory2,
} from "@mui/icons-material";

import { useSnackbar } from "notistack";
import {
  logoutUser,
  selectCurrentUser,
  selectActiveTin,
  selectActiveTinResetNotice,
  setActiveTin,
  clearActiveTinResetNotice,
} from "../redux/slices/authSlice";
import { selectUnreadCount, fetchNotifications } from "../redux/slices/notificationSlice";
import { selectMyUnclaimedPackagesCount, fetchMyUnclaimedPackages } from "../redux/slices/unclaimedPackagesSlice";
import { fetchStatusDefinitions } from "../redux/slices/statusDefinitionsSlice";
import { fetchCurrencies } from "../redux/slices/currenciesSlice";
import { fetchFeatureFlags } from "../redux/slices/systemSettingsSlice";
import usePresenceHeartbeat from "../hooks/usePresenceHeartbeat";
import AppStoreLink from "../components/common/AppStoreLink";
import ColorModeToggle from "../components/common/ColorModeToggle";
import IosAppAnnouncementDialog from "../components/common/IosAppAnnouncementDialog";
import useIosAppAnnouncement from "../hooks/useIosAppAnnouncement";
import packageJson from "../../package.json";

const SIDEBAR_W = 240;

const navItems = [
  { label: "Dashboard", icon: <Dashboard />, path: "/dashboard" },
  {
    label: "Notifications",
    icon: <Notifications />,
    path: "/dashboard/notifications",
    badgeProp: "unreadCount",
  },
  {
    label: "My Shipments",
    icon: <LocalShipping />,
    path: "/dashboard/shipments",
  },
  {
    label: "Unclaimed Packages",
    icon: <Inventory2 />,
    path: "/dashboard/unclaimed-packages",
    badgeProp: "unclaimedCount",
  },
  {
    label: "Consolidations",
    icon: <CallMerge />,
    path: "/dashboard/consolidations",
  },
  {
    label: "Upload Documents",
    icon: <UploadFile />,
    path: "/dashboard/upload",
  },
  { label: "Payments", icon: <Payments />, path: "/dashboard/payments" },
  { label: "Rates", icon: <AttachMoneyIcon />, path: "/dashboard/rates" },
  { label: "Promos", icon: <LocalOffer />, path: "/dashboard/promos" },
  { label: "Referrals", icon: <CardGiftcard />, path: "/dashboard/referrals" },
  { label: "Upgrade", icon: <WorkspacePremium />, path: "/dashboard/upgrade" },
];

// Moved OUTSIDE CustomerLayout — same fix as AdminLayout. Prevents remount
// (and scroll-position loss) on every navigation.
const SidebarContent = ({
  location,
  navigate,
  unreadCount,
  unclaimedCount,
  user,
  onNavigate,
  activeTin,
  onChangeTin,
  onOpenAppStoreDialog,
}) => (
  <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 2.25,
        py: 2.5,
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        minHeight: 64,
      }}
    >
      <Box
        component="img"
        src="/logos/Waypoint-Imports-Favicon-White.png"
        alt="Waypoint Imports"
        sx={{
          width: 44,
          height: 44,
          objectFit: "contain",
          flexShrink: 0,
        }}
      />
      <Box>
        <Typography
          sx={{
            color: "white",
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Waypoint
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.45)",
            fontSize: 9.5,
            letterSpacing: "0.5px",
            fontWeight: 500,
          }}
        >
          IMPORTS PORTAL
        </Typography>
      </Box>
    </Box>

    {/* User info — clickable through to the profile page, same destination
        as the "My Profile" nav item further down. */}
    <Box
      sx={{
        px: 2.25,
        py: 2,
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Box
        onClick={() => onNavigate("/dashboard/profile")}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
          borderRadius: 1.5,
          mx: -1,
          px: 1,
          py: 0.5,
          transition: "background-color 120ms ease",
          "&:hover": { bgcolor: "rgba(255,255,255,0.07)" },
        }}
      >
        <Avatar
          src={user?.profilePhotoUrl}
          sx={{
            width: 36,
            height: 36,
            bgcolor: "#0D629B",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {user?.firstName?.[0]}
          {user?.lastName?.[0]}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              color: "white",
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
            Customer
          </Typography>
        </Box>
        <ChevronRight sx={{ fontSize: 18, color: "rgba(255,255,255,0.35)", flexShrink: 0 }} />
      </Box>

      {user?.canManageMultipleTins && user?.tins?.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.35)",
              fontSize: 9.5,
              letterSpacing: "0.5px",
              fontWeight: 600,
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            Active TIN
          </Typography>
          <Select
            size="small"
            fullWidth
            value={activeTin || user.tins[0]?.tin || ""}
            onChange={(e) => onChangeTin(e.target.value)}
            sx={{
              color: "white",
              fontSize: 12.5,
              bgcolor: "rgba(255,255,255,0.06)",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.15)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.3)",
              },
              "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" },
            }}
          >
            {user.tins.map((t) => (
              <MenuItem key={t.tin} value={t.tin} sx={{ fontSize: 12.5 }}>
                {t.label ? `${t.tin} (${t.label})` : t.tin}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
    </Box>

    {/* flex:1 + minHeight:0 lets this section shrink inside the sidebar's
        flex column instead of pushing the footer off-screen; the nav list
        itself (not this whole section) is what actually scrolls, so the
        "My Portal" label stays pinned above it. */}
    <Box sx={{ flex: 1, minHeight: 0, py: 1, display: "flex", flexDirection: "column" }}>
      <Typography
        sx={{
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: "0.8px",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.3)",
          px: 2.25,
          pt: 2,
          pb: 0.75,
          flexShrink: 0,
        }}
      >
        My Portal
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {navItems.map((item) => {
        const isActive =
          location.pathname === item.path ||
          (item.path !== "/dashboard" &&
            location.pathname.startsWith(item.path));
        const badgeCount =
          item.badgeProp === "unreadCount"
            ? unreadCount
            : item.badgeProp === "unclaimedCount"
              ? unclaimedCount
              : item.badge;

        return (
          <ListItemButton
            key={item.path}
            onClick={() => onNavigate(item.path)}
            selected={isActive}
            sx={{
              px: 2.25,
              py: 1.125,
              position: "relative",
              color: isActive ? "white" : "rgba(255,255,255,0.65)",
              "&.Mui-selected": {
                bgcolor: "rgba(255,255,255,0.12)",
                color: "white",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: "#0D629B",
                  borderRadius: "0 2px 2px 0",
                },
              },
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.07)",
                color: "white",
              },
            }}
          >
            <ListItemIcon sx={{ color: "inherit", minWidth: 36 }}>
              {badgeCount ? (
                <Badge
                  badgeContent={badgeCount}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: 9,
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              slotProps={{
                primary: {
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                },
              }}
            />
          </ListItemButton>
        );
      })}
      </Box>
    </Box>

    <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
      <Box sx={{ px: 2.25, py: 1.5, display: "flex", justifyContent: "center" }}>
        <AppStoreLink variant="light" onClick={onOpenAppStoreDialog} />
      </Box>
      <Typography
        sx={{
          color: "rgba(255,255,255,0.3)",
          fontSize: 10.5,
          textAlign: "center",
          pb: 1.5,
        }}
      >
        v{packageJson.version}
      </Typography>
    </Box>
  </Box>
);

const CustomerLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector(selectCurrentUser);
  const unreadCount = useSelector(selectUnreadCount);
  const unclaimedCount = useSelector(selectMyUnclaimedPackagesCount);
  const activeTin = useSelector(selectActiveTin);
  const activeTinResetNotice = useSelector(selectActiveTinResetNotice);
  const handleChangeTin = (value) => dispatch(setActiveTin(value));

  const currentNavLabel = navItems.find(
    (item) => location.pathname === item.path || (item.path !== "/dashboard" && location.pathname.startsWith(item.path)),
  )?.label;
  usePresenceHeartbeat(currentNavLabel);

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchStatusDefinitions());
    dispatch(fetchCurrencies());
    dispatch(fetchFeatureFlags());
  }, [dispatch]);

  useEffect(() => {
    if (activeTinResetNotice) {
      enqueueSnackbar(activeTinResetNotice, { variant: "info" });
      dispatch(clearActiveTinResetNotice());
    }
  }, [activeTinResetNotice, dispatch, enqueueSnackbar]);

  useEffect(() => {
    dispatch(fetchNotifications({ limit: 20 }));
    const interval = setInterval(() => dispatch(fetchNotifications({ limit: 20 })), 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchMyUnclaimedPackages());
    const interval = setInterval(() => dispatch(fetchMyUnclaimedPackages()), 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate("/login");
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const {
    open: appStoreDialogOpen,
    openDialog: openAppStoreDialog,
    closeDialog: closeAppStoreDialog,
  } = useIosAppAnnouncement({ autoShowOnMount: true });

  return (
    // `position: "fixed", inset: 0` instead of `height: "100vh"` — a
    // statically-positioned Box, however tall its own CSS says it should
    // be, can still let the DOCUMENT grow taller than the viewport if any
    // descendant's layout slips past it (which is exactly what a very tall
    // page like Bulk Add did, even after fixing the inner scroll
    // container's own min-height issue) — the window/body then gets its
    // OWN scrollbar on top of `.app-scroll-container`'s, which is the
    // "two scrollbars, one dragging the header along with it" bug. Fixed
    // positioning removes this Box from document flow entirely, so it's
    // pinned to exactly the viewport no matter what happens inside it —
    // only `.app-scroll-container` can ever scroll.
    <Box sx={{ display: "flex", position: "fixed", inset: 0, overflow: "hidden" }}>
      <IosAppAnnouncementDialog open={appStoreDialogOpen} onClose={closeAppStoreDialog} />
      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_W,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: SIDEBAR_W,
            bgcolor: "#001A3C",
            border: "none",
          },
          display: { xs: "none", md: "block" },
        }}
      >
        <SidebarContent
          location={location}
          navigate={navigate}
          unreadCount={unreadCount}
          unclaimedCount={unclaimedCount}
          user={user}
          onNavigate={handleNavigate}
          activeTin={activeTin}
          onChangeTin={handleChangeTin}
          onOpenAppStoreDialog={openAppStoreDialog}
        />
      </Drawer>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: SIDEBAR_W, bgcolor: "#001A3C" },
        }}
      >
        <SidebarContent
          location={location}
          navigate={navigate}
          unreadCount={unreadCount}
          unclaimedCount={unclaimedCount}
          user={user}
          onNavigate={handleNavigate}
          activeTin={activeTin}
          onChangeTin={handleChangeTin}
          onOpenAppStoreDialog={openAppStoreDialog}
        />
      </Drawer>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <AppBar position="static" elevation={0}>
          <Toolbar sx={{ minHeight: "60px !important", px: 2.5, gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { md: "none" }, color: "text.secondary" }}
            >
              <MenuIcon />
            </IconButton>
            {/* `flex={1}` as a bare prop does nothing in this MUI version —
                only the `sx` prop is processed for Box's style props now
                (confirmed in @mui/system's styleFunctionSx, which no-ops
                unless `sx` is set). This spacer silently failed to push the
                icons right until it was wrapped in sx. */}
            <Box sx={{ flex: 1 }} />

            <ColorModeToggle />

            {user?.referralCreditBalance > 0 && (
              <Chip
                size="small"
                icon={<CardGiftcard sx={{ fontSize: 16 }} />}
                label={`CI$${user.referralCreditBalance.toFixed(2)} credit`}
                onClick={() => navigate("/dashboard/referrals")}
                sx={{
                  bgcolor: "rgba(14,159,110,0.1)",
                  color: "#0E9F6E",
                  fontWeight: 700,
                  cursor: "pointer",
                  "& .MuiChip-icon": { color: "#0E9F6E" },
                }}
              />
            )}

            {/* Notification bell and Avatar removed — both already live in
                the sidebar (Notifications nav item, account card up top),
                so this duplicated them in the navbar. Logout and the theme
                toggle are the only account-level actions not already
                reachable from the sidebar, so those stayed. */}

            {/* Previously only reachable via a menu hidden behind the avatar
                click — moved out to its own always-visible, labeled button
                so it's actually discoverable in the navbar. */}
            <Button
              size="small"
              onClick={handleLogout}
              startIcon={<Logout sx={{ fontSize: 18 }} />}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                color: "error.main",
                textTransform: "none",
                fontWeight: 600,
                px: 1.5,
              }}
            >
              Logout
            </Button>
          </Toolbar>
        </AppBar>

        {/* minHeight: 0 is load-bearing — a flex item defaults to
            min-height: auto, meaning it won't shrink below its own
            content's height even inside a flex column with a fixed
            height ancestor (see the outer Box's height: "100vh" above).
            Without this, a page with unusually tall content (e.g. the
            Bulk Add form's many stacked order/item blocks) pushes this
            container taller than the viewport, so IT also tries to
            scroll via its own overflow: auto on top of whatever else
            ends up scrolling — two competing vertical scrollbars, and
            the page visually "jumping"/cutting off when scrolling back
            up. Every other page just never had tall enough content to
            expose it. */}
        <Box className="app-scroll-container" sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 3 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerLayout;